import { useEffect, useRef } from 'react';
import { shouldRecordWatched } from '@visious/core';
import { useSession } from '../platform/session';
import { useMarkWatched } from './mutations';

/**
 * §8's "recording watched state", wired to the player: fire `POST /api/v1/auth/history/{id}` **once**,
 * when playback crosses the threshold.
 *
 * The threshold itself is core's (`shouldRecordWatched` — 30 s or 25 %, whichever first). What is here is
 * the part that is about a *session and a component lifetime* rather than about a position:
 *
 * - **Once per video, ever, per mount.** The ref is keyed by video id, so a `key`-remounted Watch page
 *   starts fresh while a re-render does not re-fire. The `POST` is cheap and idempotent — a re-mark just
 *   moves the row to the head — but doing it four times a second while a video plays would be forty
 *   requests a minute, and the instance would be right to rate-limit us.
 * - **Signed out, nothing happens at all.** Not "the call fails": no request is made. §8's signed-out
 *   behaviour says nothing is recorded while signed out, and the gate on the History *page* is not what
 *   implements that — this is.
 * - **A seek past the threshold counts**, which is `shouldRecordWatched`'s documented reading and
 *   YouTube's behaviour. Accumulating played time would need a definition of "played" that §8 does not
 *   give.
 *
 * It takes numbers rather than a player, so it stays R13-clean and testable: the caller already polls
 * `status()` four times a second for the transport bar, and this reads the same two values off it.
 */
export function useWatchedRecording(input: {
  readonly videoId: string;
  readonly positionSec: number;
  readonly durationSec: number | null;
  /** False while the plan is not playable — an upcoming premiere must not be recorded as watched. */
  readonly playable: boolean;
  /**
   * §8's "record what you watch" setting (3e). `false` means **no request at all**, the same way being
   * signed out does — and it is the same reasoning: a switch that fired the request and ignored the
   * answer would still have told the instance what was watched.
   */
  readonly mirror: boolean;
}): void {
  const { videoId, positionSec, durationSec, playable, mirror } = input;
  const session = useSession();
  const markWatched = useMarkWatched();
  const recordedRef = useRef<string | null>(null);

  const signedIn = session.ready && session.facts.signedIn;

  useEffect(() => {
    if (!signedIn || !playable || !mirror) return;
    if (recordedRef.current === videoId) return;
    if (!shouldRecordWatched({ positionSec, durationSec })) return;

    /*
     * Written **before** the await, not after. The effect re-runs on every position sample, so a flag set
     * in the callback would let the next two or three samples through while the first request is still in
     * flight — which is the debounce §8 asks for, failing in the one way that is hard to see: three
     * identical `204`s and a row that is, correctly, in the history once.
     */
    recordedRef.current = videoId;
    void markWatched(videoId);
  }, [durationSec, markWatched, mirror, playable, positionSec, signedIn, videoId]);
}
